import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';

import { useLayoutTokens } from '../hooks/useLayoutTokens';
import { getApiErrorMessage } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/** The seven user-pickable response categories. DEFERRED stays queue-machinery
 * only; UNSURE is always presented as "Don't recall" in the UI. Labels mirror
 * the response chips on the Check-In page. */
const CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: 'completed', label: 'Completed' },
  { value: 'partial', label: 'Partial' },
  { value: 'missed', label: 'Missed' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'snoozed', label: 'Snoozed' },
  { value: 'not_applicable', label: 'Not applicable' },
  { value: 'unsure', label: "Don't recall" },
];

/** Mirrors schemas.ScorePresetCreate/Update options max_length. */
const MAX_OPTIONS = 20;

interface ScorePresetOption {
  id: number;
  response_category: string;
  label: string;
  score_value: number | null;
  show_in_queue: boolean;
  sort_order: number;
}

interface ScorePreset {
  id: number;
  name: string;
  options: ScorePresetOption[];
}

/** One editable option row. The score is kept as raw text so a cleared field
 * stays distinguishable from 0 — blank posts as null (no score). */
interface OptionDraft {
  response_category: string;
  label: string;
  score: string;
  show_in_queue: boolean;
}

const draftFromOption = (option: ScorePresetOption): OptionDraft => ({
  response_category: option.response_category,
  label: option.label,
  score: option.score_value === null ? '' : String(option.score_value),
  show_in_queue: option.show_in_queue,
});

const scoreInvalid = (raw: string): boolean => {
  if (raw.trim() === '') return false;
  const value = Number(raw);
  return Number.isNaN(value) || value < 0 || value > 10;
};

const countLabel = (preset: ScorePreset): string =>
  `${preset.options.length} ${preset.options.length === 1 ? 'option' : 'options'}`;

/**
 * Profile → Score presets: the user's reusable labelled-answer sets for habit
 * check-ins. A preset is a named list of (category, label, score, visibility)
 * options; assigning one to a habit stamps the options onto it. List order is
 * the display order — the API derives sort_order from list position.
 *
 * Deleting a preset never rewrites history: habits keep their stamped answers
 * and recorded events keep the label/score they were answered with; only the
 * preset link is removed (the delete confirmation says exactly that).
 */
const ScorePresetsSection: React.FC = () => {
  const { cardContentPaddingSx } = useLayoutTokens();
  const [presets, setPresets] = useState<ScorePreset[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [rows, setRows] = useState<OptionDraft[]>([]);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<ScorePreset | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchPresets = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/check-ins/score-presets`);
      // Crash-proof the Profile render: a proxy hiccup or auth edge serving
      // a non-array body with a 200 must degrade to the empty state, not
      // take down the page on presets.map.
      setPresets(Array.isArray(res.data) ? res.data : []);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load score presets'));
    }
  }, []);

  useEffect(() => {
    fetchPresets();
  }, [fetchPresets]);

  const openCreate = () => {
    setEditingId(null);
    setName('');
    setRows([]);
    setError(null);
    setDialogOpen(true);
  };

  const openEdit = (preset: ScorePreset) => {
    setEditingId(preset.id);
    setName(preset.name);
    // The API serves options already in (sort_order, id) order.
    setRows(preset.options.map(draftFromOption));
    setError(null);
    setDialogOpen(true);
  };

  const updateRow = (index: number, patch: Partial<OptionDraft>) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      { response_category: 'completed', label: '', score: '', show_in_queue: true },
    ]);
  };

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const saveDisabled =
    saving ||
    name.trim() === '' ||
    rows.some((row) => row.label.trim() === '' || scoreInvalid(row.score));

  const save = async () => {
    const payload = {
      name: name.trim(),
      // List position is the order — the API turns it into sort_order.
      options: rows.map((row) => ({
        response_category: row.response_category,
        label: row.label.trim(),
        score_value: row.score.trim() === '' ? null : Number(row.score),
        show_in_queue: row.show_in_queue,
      })),
    };
    setSaving(true);
    try {
      if (editingId === null) {
        await axios.post(`${API_BASE_URL}/api/check-ins/score-presets`, payload);
      } else {
        await axios.put(
          `${API_BASE_URL}/api/check-ins/score-presets/${editingId}`,
          payload,
        );
      }
      setDialogOpen(false);
      setError(null);
      await fetchPresets();
    } catch (err: any) {
      // Duplicate name (409) and duplicate (category, label) pairs (422)
      // both land here — the dialog stays open for a correction.
      setError(getApiErrorMessage(err, 'Failed to save the score preset'));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${API_BASE_URL}/api/check-ins/score-presets/${deleteTarget.id}`,
      );
      setError(null);
      await fetchPresets();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to delete the score preset'));
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  // A single error slot: shown inside the edit dialog while it is open (a
  // failed save keeps it open), in the card body otherwise.
  const errorAlert = error && (
    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
      {error}
    </Alert>
  );

  return (
    <Card>
      <CardContent sx={cardContentPaddingSx}>
        <Typography variant="h6" gutterBottom>
          Score presets
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Reusable answer sets for habit check-ins. Each option pairs a response
          category with your own label and an optional 0–10 score; assign a
          score preset to a habit to offer these answers in its queue.
        </Typography>

        {!dialogOpen && errorAlert}

        {presets.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No score presets yet.
          </Typography>
        ) : (
          <Stack spacing={1} sx={{ mb: 2 }}>
            {presets.map((preset) => (
              <Stack
                key={preset.id}
                direction="row"
                alignItems="center"
                spacing={1}
              >
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body1" noWrap>
                    {preset.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {countLabel(preset)}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  aria-label={`Edit ${preset.name}`}
                  onClick={() => openEdit(preset)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label={`Delete ${preset.name}`}
                  onClick={() => setDeleteTarget(preset)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        )}

        <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreate}>
          New score preset
        </Button>

        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            {editingId === null ? 'New score preset' : 'Edit score preset'}
          </DialogTitle>
          <DialogContent>
            {errorAlert}
            <TextField
              autoFocus
              fullWidth
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ mt: 1, mb: 2 }}
            />

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Options are offered in this order. Leave the score blank for an
              answer with no score.
            </Typography>

            <Stack spacing={1.5}>
              {rows.map((row, idx) => (
                <Stack
                  // Index-keyed on purpose: drafts have no identity and rows
                  // can be removed from the middle.
                  key={idx}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                >
                  <FormControl size="small" sx={{ width: 160 }}>
                    <InputLabel id={`score-preset-option-category-${idx}`}>
                      Category
                    </InputLabel>
                    <Select
                      labelId={`score-preset-option-category-${idx}`}
                      label="Category"
                      value={row.response_category}
                      onChange={(e) =>
                        updateRow(idx, { response_category: String(e.target.value) })
                      }
                    >
                      {CATEGORY_OPTIONS.map((category) => (
                        <MenuItem key={category.value} value={category.value}>
                          {category.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label="Label"
                    value={row.label}
                    onChange={(e) => updateRow(idx, { label: e.target.value })}
                    sx={{ flexGrow: 1, minWidth: 160 }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    label="Score"
                    value={row.score}
                    onChange={(e) => updateRow(idx, { score: e.target.value })}
                    inputProps={{ min: 0, max: 10, step: 0.1 }}
                    sx={{ width: 100 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={row.show_in_queue}
                        onChange={(e) =>
                          updateRow(idx, { show_in_queue: e.target.checked })
                        }
                      />
                    }
                    label="Show in queue"
                  />
                  <IconButton
                    size="small"
                    aria-label={`Remove option ${idx + 1}`}
                    onClick={() => removeRow(idx)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>

            <Box sx={{ mt: 1.5 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addRow}
                disabled={rows.length >= MAX_OPTIONS}
              >
                Add option
              </Button>
              {rows.length >= MAX_OPTIONS && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  A score preset holds at most {MAX_OPTIONS} options.
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={save} disabled={saveDisabled}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
          <DialogTitle>Delete score preset?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Deleting &quot;{deleteTarget?.name}&quot; removes only the link
              between this score preset and any habits using it. Habits keep
              their stamped answers, and nothing already recorded changes.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              onClick={confirmDelete}
              disabled={deleting}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default ScorePresetsSection;
